/* global axios */
import ApiClient from './ApiClient';

class NooviConnectAPI extends ApiClient {
  constructor() {
    super('noovi_connect', { accountScoped: true });
  }

  listSessions(accountId) {
    return axios.get(`${this.apiVersion}/accounts/${accountId}/noovi_connect`);
  }

  getStatus(accountId, inboxId) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/status`
    );
  }

  getProfile(accountId, inboxId) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/profile`
    );
  }

  checkNumber(accountId, inboxId, phone) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/check_number`,
      { params: { phone } }
    );
  }

  getLabels(accountId, inboxId) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/labels`
    );
  }

  getLabelChats(accountId, inboxId, labelId) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/label_chats`,
      { params: { label_id: labelId } }
    );
  }

  getGroupInfoFromLink(accountId, inboxId, link) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/group_info_from_link`,
      { params: { link } }
    );
  }

  joinGroupWithLink(accountId, inboxId, link) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/join_group_with_link`,
      { link }
    );
  }

  setPushname(accountId, inboxId, pushName) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/set_pushname`,
      { push_name: pushName }
    );
  }

  setProfileStatus(accountId, inboxId, status) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/set_profile_status`,
      { status }
    );
  }

  listGroups(accountId, inboxId) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/groups`
    );
  }

  refreshQr(accountId, inboxId) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/refresh_qr`
    );
  }

  // QR image as a blob (dashboard auth is header-based, so a plain <img src>
  // cannot authenticate — we fetch via axios and build an object URL).
  getQr(accountId, inboxId) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/qr`,
      { responseType: 'blob' }
    );
  }

  requestPairingCode(accountId, inboxId, phone) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/request_pairing_code`,
      { phone }
    );
  }

  // C1: reject an inbound WhatsApp call. callId/from come from the call activity metadata.
  rejectCall(accountId, inboxId, callId, from) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/reject_call`,
      { call_id: callId, from }
    );
  }

  // ── Group management (Phase 2) ─────────────────────────────────────────────

  createGroup(accountId, inboxId, { title, participants }) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/create_group`,
      { title, participants }
    );
  }

  getGroupParticipants(accountId, inboxId, groupJid) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/group_participants`,
      { params: { group_jid: groupJid } }
    );
  }

  getGroupPicture(accountId, inboxId, groupJid) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/group_picture`,
      { params: { group_jid: groupJid } }
    );
  }

  // Send a poll to a chat/group. `phone` accepts a bare number or a JID (e.g. a @g.us group).
  sendPoll(accountId, inboxId, { phone, question, options, maxAnswer = 1 }) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/send_poll`,
      { phone, question, options, max_answer: maxAnswer }
    );
  }

  getGroupInviteLink(accountId, inboxId, groupJid, reset = false) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/group_invite_link`,
      { params: { group_jid: groupJid, reset } }
    );
  }

  setGroupName(accountId, inboxId, groupJid, name) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/set_group_name`,
      { group_jid: groupJid, name }
    );
  }

  setGroupTopic(accountId, inboxId, groupJid, topic) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/set_group_topic`,
      { group_jid: groupJid, topic }
    );
  }

  setGroupLocked(accountId, inboxId, groupJid, locked) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/set_group_locked`,
      { group_jid: groupJid, locked }
    );
  }

  setGroupAnnounce(accountId, inboxId, groupJid, announce) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/set_group_announce`,
      { group_jid: groupJid, announce }
    );
  }

  setGroupPhoto(accountId, inboxId, groupJid, file) {
    const formData = new FormData();
    formData.append('group_jid', groupJid);
    if (file) formData.append('photo', file);
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/set_group_photo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  leaveGroup(accountId, inboxId, groupJid) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/leave_group`,
      { group_jid: groupJid }
    );
  }

  addParticipants(accountId, inboxId, groupJid, phones) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/add_participants`,
      { group_jid: groupJid, phones }
    );
  }

  removeParticipants(accountId, inboxId, groupJid, phones) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/remove_participants`,
      { group_jid: groupJid, phones }
    );
  }

  promoteParticipants(accountId, inboxId, groupJid, phones) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/promote_participants`,
      { group_jid: groupJid, phones }
    );
  }

  demoteParticipants(accountId, inboxId, groupJid, phones) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/demote_participants`,
      { group_jid: groupJid, phones }
    );
  }

  getParticipantRequests(accountId, inboxId, groupJid) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/participant_requests`,
      { params: { group_jid: groupJid } }
    );
  }

  approveParticipantRequests(accountId, inboxId, groupJid, phones) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/approve_participant_requests`,
      { group_jid: groupJid, phones }
    );
  }

  rejectParticipantRequests(accountId, inboxId, groupJid, phones) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/reject_participant_requests`,
      { group_jid: groupJid, phones }
    );
  }

  // ── Hub report (Phase 7) ───────────────────────────────────────────────────

  hubReport(accountId, inboxId) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/hub_report`
    );
  }

  // ── Channel (newsletter) management (Phase 3) ──────────────────────────────

  listNewsletters(accountId, inboxId) {
    return axios.get(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/newsletters`
    );
  }

  unfollowNewsletter(accountId, inboxId, newsletterId) {
    return axios.post(
      `${this.apiVersion}/accounts/${accountId}/noovi_connect/${inboxId}/unfollow_newsletter`,
      { newsletter_id: newsletterId }
    );
  }
}

export default new NooviConnectAPI();
